import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient, API_BASE } from '../api/client';
import { useMediaQuery } from '../hooks/useMediaQuery';

// ── Type Definitions ──────────────────────────────────────────

interface VendorEventRegistration {
  boothNumber: string | null;
  zone: string | null;
  event: { id: string; name: string };
}

interface OfferingVendor {
  id: string;
  businessName: string;
  category: string | null;
  phone: string | null;
  eventRegistrations: VendorEventRegistration[];
}

interface VendorOffering {
  id: string;
  vendorId: string;
  price: number;
  available: boolean;
  notes: string | null;
  vendor: OfferingVendor;
}

interface EventLite {
  id: string;
  name: string;
  date?: string;
}

interface FoodEventLink {
  id: string;
  eventId: string;
  event?: EventLite;
}

interface FoodMenuItem {
  id: string;
  name: string;
  description: string | null;
  category: string | null;
  imageUrl: string | null;
  tags: string | null;
  isActive: boolean;
  sortOrder: number;
  vendorOfferings: VendorOffering[];
  eventLinks?: FoodEventLink[];
}

interface FoodCategory {
  name: string;
  count: number;
}

interface FoodCatalogResponse {
  menuItems: FoodMenuItem[];
  categories: FoodCategory[];
  stats: {
    totalDishes: number;
    totalOfferings: number;
    totalVendors: number;
  };
}

// ── Helpers ───────────────────────────────────────────────────

function resolveImageUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${API_BASE}${url}`;
}

function formatPrice(price: number | null): string {
  if (price === null || price === undefined) return '';
  const num = Number(price);
  if (num === 0) return '';
  return `${num.toFixed(0)} KDU$`;
}

function priceRange(offerings: VendorOffering[]): string {
  if (offerings.length === 0) return '';
  const prices = offerings.map((o) => Number(o.price));
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  if (min === max) return formatPrice(min);
  return `${min.toFixed(0)}–${max.toFixed(0)} KDU$`;
}

const FOOD_CATEGORY_ICONS: Record<string, string> = {
  'Appetizers': '🥗',
  'Main Dishes': '🍛',
  'Sides': '🍚',
  'Desserts': '🍰',
  'Drinks': '🥤',
  'Snacks': '🍿',
  'Traditional': '🍲',
  'Grilled': '🔥',
  'Seafood': '🐟',
  'Vegetarian': '🥬',
  'Soups': '🥣',
  'Rice Dishes': '🍚',
  'Street Food': '🥙',
  'Uncategorized': '🍴',
};

function getCategoryIcon(category: string | null): string {
  if (!category) return '🍴';
  return FOOD_CATEGORY_ICONS[category] || '🍽️';
}

// ── Inline Styles ─────────────────────────────────────────────

const s: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #fff8f0 0%, #fff3e0 50%, #f0f7f2 100%)',
    paddingBottom: '2rem',
    maxWidth: '1200px',
    margin: '0 auto',
    width: '100%',
    overflowX: 'hidden' as const,
    boxSizing: 'border-box' as const,
  },
  header: {
    background: 'linear-gradient(135deg, #bf360c 0%, #e65100 40%, #f57c00 100%)',
    padding: '1.5rem 1rem 1.25rem',
    borderRadius: '0 0 24px 24px',
    marginBottom: '1rem',
    position: 'relative' as const,
    overflow: 'hidden' as const,
  },
  headerPattern: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    opacity: 0.08,
    backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 20px, #fff 20px, #fff 21px)',
  },
  headerContent: {
    position: 'relative' as const,
    zIndex: 1,
  },
  headerTitle: {
    fontSize: '1.6rem',
    fontWeight: 700,
    color: '#fff',
    margin: '0 0 0.25rem 0',
  },
  headerSubtitle: {
    fontSize: '0.85rem',
    color: '#ffcc80',
    margin: '0 0 0.5rem 0',
  },
  headerNote: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.3rem',
    fontSize: '0.72rem',
    color: '#fff',
    backgroundColor: 'rgba(255,255,255,0.15)',
    padding: '0.3rem 0.6rem',
    borderRadius: '8px',
    backdropFilter: 'blur(4px)',
  },
  searchBar: {
    margin: '0 1rem',
    position: 'relative' as const,
    marginTop: '-1.25rem',
    zIndex: 2,
  },
  searchInput: {
    width: '100%',
    padding: '0.75rem 1rem 0.75rem 2.5rem',
    border: 'none',
    borderRadius: '16px',
    fontSize: '0.9rem',
    backgroundColor: '#fff',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    outline: 'none',
    boxSizing: 'border-box' as const,
  },
  searchIcon: {
    position: 'absolute' as const,
    left: '1rem',
    top: '50%',
    transform: 'translateY(-50%)',
    fontSize: '1rem',
    opacity: 0.5,
  },
  statsRow: {
    display: 'flex',
    gap: '0.5rem',
    padding: '0.75rem 1rem',
    overflowX: 'auto' as const,
    scrollbarWidth: 'none' as const,
    maxWidth: '100%',
    boxSizing: 'border-box' as const,
  },
  statPill: {
    flexShrink: 0,
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '0.5rem 0.75rem',
    boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
    textAlign: 'center' as const,
    minWidth: '75px',
  },
  statNumber: {
    fontSize: '1.1rem',
    fontWeight: 700,
    color: '#e65100',
  },
  statLabel: {
    fontSize: '0.65rem',
    color: '#6b7280',
    marginTop: '0.1rem',
  },
  categoryScroll: {
    display: 'flex',
    gap: '0.5rem',
    padding: '0.5rem 1rem 0.75rem',
    overflowX: 'auto' as const,
    WebkitOverflowScrolling: 'touch' as const,
    scrollbarWidth: 'none' as const,
    maxWidth: '100%',
    boxSizing: 'border-box' as const,
  },
  categoryChip: {
    flexShrink: 0,
    padding: '0.45rem 0.85rem',
    borderRadius: '20px',
    fontSize: '0.8rem',
    fontWeight: 500,
    border: '1.5px solid #e0e0e0',
    backgroundColor: '#fff',
    color: '#555',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    whiteSpace: 'nowrap' as const,
    display: 'flex',
    alignItems: 'center',
    gap: '0.3rem',
  },
  categoryChipActive: {
    flexShrink: 0,
    padding: '0.45rem 0.85rem',
    borderRadius: '20px',
    fontSize: '0.8rem',
    fontWeight: 600,
    border: '1.5px solid #e65100',
    backgroundColor: '#e65100',
    color: '#fff',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    whiteSpace: 'nowrap' as const,
    display: 'flex',
    alignItems: 'center',
    gap: '0.3rem',
  },
  chipCount: {
    fontSize: '0.65rem',
    opacity: 0.8,
  },
  eventChip: {
    flexShrink: 0,
    padding: '0.45rem 0.85rem',
    borderRadius: '20px',
    fontSize: '0.8rem',
    fontWeight: 500,
    border: '1.5px solid #bbdefb',
    backgroundColor: '#fff',
    color: '#1565c0',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    whiteSpace: 'nowrap' as const,
    display: 'flex',
    alignItems: 'center',
    gap: '0.3rem',
  },
  eventChipActive: {
    flexShrink: 0,
    padding: '0.45rem 0.85rem',
    borderRadius: '20px',
    fontSize: '0.8rem',
    fontWeight: 600,
    border: '1.5px solid #1565c0',
    backgroundColor: '#1565c0',
    color: '#fff',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    whiteSpace: 'nowrap' as const,
    display: 'flex',
    alignItems: 'center',
    gap: '0.3rem',
  },
  eventPill: {
    display: 'inline-block',
    fontSize: '0.62rem',
    fontWeight: 600,
    backgroundColor: '#e3f2fd',
    color: '#1565c0',
    padding: '0.12rem 0.45rem',
    borderRadius: '6px',
  },
  eventPillRow: {
    display: 'flex',
    gap: '0.25rem',
    flexWrap: 'wrap' as const,
    marginTop: '0.35rem',
  },
  infoBar: {
    margin: '0 1rem 0.75rem',
    backgroundColor: '#fff8e1',
    border: '1px solid #ffe082',
    borderRadius: '12px',
    padding: '0.6rem 0.85rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.78rem',
    color: '#5d4037',
  },
  // Dish cards
  dishGrid: {
    padding: '0 1rem',
    display: 'grid',
    // min(100%, 340px) prevents the 340px track from overflowing narrow phones —
    // on small screens columns collapse to a single full-width column.
    gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 340px), 1fr))',
    gap: '1rem',
    boxSizing: 'border-box' as const,
  },
  dishCard: {
    backgroundColor: '#fff',
    borderRadius: '20px',
    boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
    overflow: 'hidden' as const,
    transition: 'transform 0.2s ease',
  },
  dishHeader: {
    display: 'flex',
    gap: '0.75rem',
    padding: '1rem',
    cursor: 'pointer',
  },
  dishImage: {
    width: '80px',
    height: '80px',
    borderRadius: '14px',
    objectFit: 'cover' as const,
    backgroundColor: '#fff3e0',
    flexShrink: 0,
  },
  dishImagePlaceholder: {
    width: '80px',
    height: '80px',
    borderRadius: '14px',
    background: 'linear-gradient(135deg, #fff3e0, #ffe0b2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '2rem',
    flexShrink: 0,
  },
  dishInfo: {
    flex: 1,
    minWidth: 0,
  },
  dishName: {
    fontSize: '1.05rem',
    fontWeight: 700,
    color: '#1a1a1a',
    margin: '0 0 0.2rem 0',
  },
  dishCategory: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.2rem',
    fontSize: '0.72rem',
    color: '#6b7280',
    backgroundColor: '#f3f4f6',
    padding: '0.12rem 0.5rem',
    borderRadius: '8px',
    marginBottom: '0.3rem',
  },
  dishDesc: {
    fontSize: '0.78rem',
    color: '#6b7280',
    lineHeight: '1.4',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical' as const,
    overflow: 'hidden' as const,
    margin: '0.2rem 0 0',
  },
  dishPriceRange: {
    fontSize: '0.85rem',
    fontWeight: 700,
    color: '#e65100',
    marginTop: '0.3rem',
  },
  dishTags: {
    display: 'flex',
    gap: '0.25rem',
    flexWrap: 'wrap' as const,
    marginTop: '0.3rem',
  },
  tag: {
    fontSize: '0.6rem',
    backgroundColor: '#fff3e0',
    color: '#e65100',
    padding: '0.1rem 0.4rem',
    borderRadius: '6px',
  },
  // Vendor offerings list
  offeringsSection: {
    borderTop: '1px solid #f0f0f0',
    padding: '0.75rem 1rem',
  },
  offeringsTitle: {
    fontSize: '0.7rem',
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    marginBottom: '0.5rem',
  },
  offeringRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.5rem 0',
    borderBottom: '1px solid #fafafa',
  },
  offeringVendor: {
    flex: 1,
    minWidth: 0,
  },
  offeringVendorName: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#1a1a1a',
    overflow: 'hidden' as const,
    textOverflow: 'ellipsis' as const,
    whiteSpace: 'nowrap' as const,
  },
  offeringBooth: {
    fontSize: '0.7rem',
    color: '#e65100',
    marginTop: '0.1rem',
  },
  offeringNotes: {
    fontSize: '0.7rem',
    color: '#6b7280',
    fontStyle: 'italic' as const,
  },
  offeringPrice: {
    fontSize: '1rem',
    fontWeight: 700,
    color: '#e65100',
    flexShrink: 0,
    marginLeft: '0.75rem',
  },
  vendorCount: {
    fontSize: '0.72rem',
    color: '#6b7280',
    marginTop: '0.2rem',
  },
  // Detail modal
  modalOverlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.6)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem',
  },
  modalSheet: {
    backgroundColor: '#fff',
    borderRadius: '20px',
    width: '100%',
    maxWidth: '500px',
    maxHeight: '85vh',
    overflowY: 'auto' as const,
    position: 'relative' as const,
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  modalClose: {
    position: 'absolute' as const,
    top: '12px',
    right: '12px',
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: 'rgba(0,0,0,0.5)',
    color: '#fff',
    border: 'none',
    fontSize: '1rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
  },
  modalImage: {
    width: '100%',
    maxHeight: '250px',
    objectFit: 'cover' as const,
    backgroundColor: '#fff3e0',
    display: 'block',
  },
  modalImagePlaceholder: {
    width: '100%',
    height: '180px',
    background: 'linear-gradient(135deg, #fff3e0, #ffe0b2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '4rem',
  },
  modalBody: {
    padding: '1.25rem',
  },
  modalTitle: {
    fontSize: '1.3rem',
    fontWeight: 700,
    color: '#1a1a1a',
    margin: '0 0 0.3rem 0',
  },
  modalCategory: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.3rem',
    fontSize: '0.8rem',
    color: '#6b7280',
    backgroundColor: '#f3f4f6',
    padding: '0.2rem 0.6rem',
    borderRadius: '8px',
    marginBottom: '0.75rem',
  },
  modalDesc: {
    fontSize: '0.9rem',
    color: '#333',
    lineHeight: '1.6',
    marginBottom: '1rem',
  },
  modalTags: {
    display: 'flex',
    gap: '0.4rem',
    flexWrap: 'wrap' as const,
    marginBottom: '1rem',
  },
  modalTag: {
    fontSize: '0.75rem',
    backgroundColor: '#fff3e0',
    color: '#e65100',
    padding: '0.25rem 0.6rem',
    borderRadius: '8px',
    fontWeight: 500,
  },
  modalOfferingsHeader: {
    fontSize: '0.8rem',
    fontWeight: 700,
    color: '#1a1a1a',
    marginBottom: '0.5rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.3rem',
  },
  modalOfferingCard: {
    backgroundColor: '#fafafa',
    borderRadius: '12px',
    padding: '0.75rem 1rem',
    marginBottom: '0.5rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  modalOfferingVendorName: {
    fontSize: '0.9rem',
    fontWeight: 600,
    color: '#1a1a1a',
  },
  modalOfferingBooth: {
    fontSize: '0.75rem',
    color: '#e65100',
    marginTop: '0.15rem',
  },
  modalOfferingNotes: {
    fontSize: '0.75rem',
    color: '#6b7280',
    fontStyle: 'italic' as const,
    marginTop: '0.1rem',
  },
  modalOfferingPrice: {
    fontSize: '1.2rem',
    fontWeight: 700,
    color: '#e65100',
  },
  // Empty & loading
  empty: {
    textAlign: 'center' as const,
    padding: '3rem 1rem',
    color: '#6b7280',
    fontSize: '0.9rem',
  },
  emptyIcon: {
    fontSize: '3rem',
    marginBottom: '0.75rem',
    display: 'block',
  },
  loadingSpinner: {
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'center',
    alignItems: 'center',
    padding: '3rem',
    gap: '0.75rem',
    color: '#e65100',
  },
};

// ── Component ─────────────────────────────────────────────────

export function FoodCatalogPage() {
  const isMobile = useMediaQuery(768);
  const [data, setData] = useState<FoodCatalogResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filter state
  const [searchText, setSearchText] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('');
  const [activeEventId, setActiveEventId] = useState<string>('');

  // Events list (for the event filter chips)
  const [events, setEvents] = useState<EventLite[]>([]);

  // Detail modal
  const [selectedDish, setSelectedDish] = useState<FoodMenuItem | null>(null);

  // Debounced search
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Touch for swipe dismiss
  const touchStartY = useRef(0);

  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchText);
    }, 300);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [searchText]);

  const fetchCatalog = useCallback(async () => {
    try {
      setError('');
      const params = new URLSearchParams();
      if (activeCategory) params.set('category', activeCategory);
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (activeEventId) params.set('eventId', activeEventId);
      const query = params.toString() ? `?${params.toString()}` : '';
      const result = await apiClient<FoodCatalogResponse>(`/food-catalog${query}`);
      setData(result);
    } catch (err: any) {
      setError(err?.message || 'Failed to load food catalog');
      setData({ menuItems: [], categories: [], stats: { totalDishes: 0, totalOfferings: 0, totalVendors: 0 } });
    } finally {
      setLoading(false);
    }
  }, [activeCategory, debouncedSearch, activeEventId]);

  // Only show the full-page loader on the very first load. Subsequent
  // searches/filter changes refresh data in place so the search input keeps
  // focus and the page doesn't flash the loading screen (was causing "jumpy" typing).
  const initialLoadDone = useRef(false);
  useEffect(() => {
    if (!initialLoadDone.current) {
      setLoading(true);
    }
    fetchCatalog().finally(() => {
      initialLoadDone.current = true;
    });
  }, [fetchCatalog]);

  // Load events once for the event filter
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await apiClient<EventLite[]>('/events');
        if (!cancelled) setEvents(Array.isArray(result) ? result : []);
      } catch {
        if (!cancelled) setEvents([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Handlers ──

  const openDetail = (dish: FoodMenuItem) => setSelectedDish(dish);
  const closeDetail = () => setSelectedDish(null);

  const handleModalTouchStart = (e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
  };
  const handleModalTouchEnd = (e: React.TouchEvent) => {
    if (e.changedTouches[0].clientY - touchStartY.current > 100) closeDetail();
  };

  // ── Derived data ──

  const menuItems = data?.menuItems || [];
  const categories = data?.categories || [];
  const stats = data?.stats || { totalDishes: 0, totalOfferings: 0, totalVendors: 0 };

  // ── Render: Dish Card ──

  const renderDishCard = (dish: FoodMenuItem) => {
    return (
      <div key={dish.id} style={s.dishCard}>
        <div
          style={s.dishHeader}
          onClick={() => openDetail(dish)}
          role="button"
          tabIndex={0}
          aria-label={`${dish.name} — ${dish.vendorOfferings.length} vendor${dish.vendorOfferings.length !== 1 ? 's' : ''}`}
          onKeyDown={(e) => e.key === 'Enter' && openDetail(dish)}
        >
          {dish.imageUrl ? (
            <img
              src={resolveImageUrl(dish.imageUrl)!}
              alt={dish.name}
              style={s.dishImage}
              loading="lazy"
            />
          ) : (
            <div style={s.dishImagePlaceholder}>
              {getCategoryIcon(dish.category)}
            </div>
          )}
          <div style={s.dishInfo}>
            <h3 style={s.dishName}>{dish.name}</h3>
            {dish.category && (
              <span style={s.dishCategory}>
                {getCategoryIcon(dish.category)} {dish.category}
              </span>
            )}
            {dish.description && <p style={s.dishDesc}>{dish.description}</p>}
            {dish.vendorOfferings.length > 0 && (
              <div style={s.dishPriceRange}>{priceRange(dish.vendorOfferings)}</div>
            )}
            <div style={s.vendorCount}>
              {dish.vendorOfferings.length} vendor{dish.vendorOfferings.length !== 1 ? 's' : ''} offering this dish
            </div>
            {dish.tags && (
              <div style={s.dishTags}>
                {dish.tags.split(',').slice(0, 4).map((tag, i) => (
                  <span key={i} style={s.tag}>{tag.trim()}</span>
                ))}
              </div>
            )}
            {dish.eventLinks && dish.eventLinks.length > 0 && (
              <div style={s.eventPillRow}>
                {dish.eventLinks.map((link) => (
                  <span key={link.id} style={s.eventPill}>
                    📅 {link.event?.name || 'Event'}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Vendor offerings preview (first 3) */}
        {dish.vendorOfferings.length > 0 && (
          <div style={s.offeringsSection}>
            <div style={s.offeringsTitle}>
              Available at {dish.vendorOfferings.length} station{dish.vendorOfferings.length !== 1 ? 's' : ''}
            </div>
            {dish.vendorOfferings.slice(0, 3).map((offering) => {
              const booth = offering.vendor.eventRegistrations?.find((r) => r.boothNumber);
              return (
                <div key={offering.id} style={s.offeringRow}>
                  <div style={s.offeringVendor}>
                    <div style={s.offeringVendorName}>{offering.vendor.businessName}</div>
                    {booth && (
                      <div style={s.offeringBooth}>
                        📍 Station {booth.boothNumber}{booth.zone ? ` · ${booth.zone}` : ''}
                      </div>
                    )}
                    {offering.notes && (
                      <div style={s.offeringNotes}>{offering.notes}</div>
                    )}
                  </div>
                  <div style={s.offeringPrice}>{formatPrice(offering.price)}</div>
                </div>
              );
            })}
            {dish.vendorOfferings.length > 3 && (
              <div
                style={{ fontSize: '0.75rem', color: '#e65100', fontWeight: 600, textAlign: 'center', padding: '0.4rem 0', cursor: 'pointer' }}
                onClick={() => openDetail(dish)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && openDetail(dish)}
              >
                + {dish.vendorOfferings.length - 3} more vendor{dish.vendorOfferings.length - 3 !== 1 ? 's' : ''} — tap to compare
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // ── Render: Detail Modal ──

  const renderDetailModal = () => {
    if (!selectedDish) return null;
    const dish = selectedDish;

    return (
      <div
        style={s.modalOverlay}
        onClick={(e) => e.target === e.currentTarget && closeDetail()}
        role="dialog"
        aria-modal="true"
        aria-label={`${dish.name} — vendor comparison`}
      >
        <div
          style={s.modalSheet}
          onTouchStart={handleModalTouchStart}
          onTouchEnd={handleModalTouchEnd}
        >
          <button
            style={s.modalClose}
            onClick={closeDetail}
            aria-label="Close detail view"
          >
            ✕
          </button>

          {dish.imageUrl ? (
            <img
              src={resolveImageUrl(dish.imageUrl)!}
              alt={dish.name}
              style={s.modalImage}
            />
          ) : (
            <div style={s.modalImagePlaceholder}>
              {getCategoryIcon(dish.category)}
            </div>
          )}

          <div style={s.modalBody}>
            <h2 style={s.modalTitle}>{dish.name}</h2>
            {dish.category && (
              <div style={s.modalCategory}>
                {getCategoryIcon(dish.category)} {dish.category}
              </div>
            )}

            {dish.description && (
              <p style={s.modalDesc}>{dish.description}</p>
            )}

            {dish.tags && (
              <div style={s.modalTags}>
                {dish.tags.split(',').map((tag, i) => (
                  <span key={i} style={s.modalTag}>{tag.trim()}</span>
                ))}
              </div>
            )}

            {dish.eventLinks && dish.eventLinks.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.4rem' }}>
                  Served At
                </div>
                <div style={s.eventPillRow}>
                  {dish.eventLinks.map((link) => (
                    <span key={link.id} style={{ ...s.eventPill, fontSize: '0.75rem', padding: '0.25rem 0.6rem' }}>
                      📅 {link.event?.name || 'Event'}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* All vendor offerings */}
            <div style={s.modalOfferingsHeader}>
              🏪 Compare Prices ({dish.vendorOfferings.length} vendor{dish.vendorOfferings.length !== 1 ? 's' : ''})
            </div>

            {dish.vendorOfferings.length === 0 ? (
              <div style={{ fontSize: '0.85rem', color: '#6b7280', padding: '1rem 0' }}>
                No vendors currently offering this dish.
              </div>
            ) : (
              dish.vendorOfferings.map((offering) => {
                const booth = offering.vendor.eventRegistrations?.find((r) => r.boothNumber);
                return (
                  <div key={offering.id} style={s.modalOfferingCard}>
                    <div>
                      <div style={s.modalOfferingVendorName}>{offering.vendor.businessName}</div>
                      {booth && (
                        <div style={s.modalOfferingBooth}>
                          📍 Station {booth.boothNumber}{booth.zone ? ` · ${booth.zone} Zone` : ''}
                        </div>
                      )}
                      {offering.notes && (
                        <div style={s.modalOfferingNotes}>{offering.notes}</div>
                      )}
                    </div>
                    <div style={s.modalOfferingPrice}>{formatPrice(offering.price)}</div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    );
  };

  // ── Main Render ──

  if (loading) {
    return (
      <div className="attendee-page" style={s.page}>
        <div style={s.header}>
          <div style={s.headerPattern} />
          <div style={s.headerContent}>
            <h1 style={s.headerTitle}>🍽️ Food Catalog</h1>
            <p style={s.headerSubtitle}>Awujoh Food Festival Menu</p>
          </div>
        </div>
        <div style={s.loadingSpinner}>
          <span style={{ fontSize: '2rem' }}>🍲</span>
          Loading menu...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="attendee-page" style={s.page}>
        <div style={s.header}>
          <div style={s.headerPattern} />
          <div style={s.headerContent}>
            <h1 style={s.headerTitle}>🍽️ Food Catalog</h1>
            <p style={s.headerSubtitle}>Awujoh Food Festival Menu</p>
          </div>
        </div>
        <div style={{ ...s.empty, color: '#d32f2f' }}>
          <span style={s.emptyIcon}>⚠️</span>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="attendee-page" style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <div style={s.headerPattern} />
        <div style={s.headerContent}>
          <h1 style={s.headerTitle}>🍽️ Food Catalog</h1>
          <p style={s.headerSubtitle}>Awujoh Food Festival — Official Menu</p>
          <div style={s.headerNote}>
            <span>💰</span> All food purchased with KDU GA Money (available at Arena ticket booth)
          </div>
        </div>
      </div>

      {/* Search */}
      <div style={s.searchBar}>
        <span style={s.searchIcon}>🔍</span>
        <input
          style={s.searchInput}
          type="text"
          placeholder="Search dishes or ingredients..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          aria-label="Search food catalog"
        />
      </div>

      {/* Stats pills */}
      <div style={s.statsRow}>
        <div style={s.statPill}>
          <div style={s.statNumber}>{stats.totalDishes}</div>
          <div style={s.statLabel}>Dishes</div>
        </div>
        <div style={s.statPill}>
          <div style={s.statNumber}>{stats.totalVendors}</div>
          <div style={s.statLabel}>Vendors</div>
        </div>
        <div style={s.statPill}>
          <div style={s.statNumber}>{stats.totalOfferings}</div>
          <div style={s.statLabel}>Offerings</div>
        </div>
      </div>

      {/* Category filter chips */}
      {categories.length > 0 && (
        <nav style={s.categoryScroll} aria-label="Food categories">
          <button
            style={!activeCategory ? s.categoryChipActive : s.categoryChip}
            onClick={() => setActiveCategory('')}
            aria-current={!activeCategory ? 'page' : undefined}
          >
            🍽️ All
          </button>
          {categories.map((cat) => (
            <button
              key={cat.name}
              style={activeCategory === cat.name ? s.categoryChipActive : s.categoryChip}
              onClick={() => setActiveCategory(cat.name === activeCategory ? '' : cat.name)}
              aria-current={activeCategory === cat.name ? 'page' : undefined}
            >
              {getCategoryIcon(cat.name)} {cat.name}
              <span style={s.chipCount}>({cat.count})</span>
            </button>
          ))}
        </nav>
      )}

      {/* Event filter chips */}
      {events.length > 0 && (
        <nav style={s.categoryScroll} aria-label="Filter food by event">
          <button
            style={!activeEventId ? s.eventChipActive : s.eventChip}
            onClick={() => setActiveEventId('')}
            aria-current={!activeEventId ? 'page' : undefined}
          >
            📅 All Events
          </button>
          {events.map((ev) => (
            <button
              key={ev.id}
              style={activeEventId === ev.id ? s.eventChipActive : s.eventChip}
              onClick={() => setActiveEventId(ev.id === activeEventId ? '' : ev.id)}
              aria-current={activeEventId === ev.id ? 'page' : undefined}
            >
              {ev.name}
            </button>
          ))}
        </nav>
      )}

      {/* Info bar */}
      <div style={s.infoBar}>
        <span>💡</span>
        <span>No cash at food stations. Purchase KDU GA money at the Arena ticket booth. Compare prices across vendors below!</span>
      </div>

      {/* Dish cards */}
      {menuItems.length > 0 ? (
        <div style={{ ...s.dishGrid, gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(min(100%, 340px), 1fr))' }}>
          {menuItems.map(renderDishCard)}
        </div>
      ) : (
        <div style={s.empty}>
          <span style={s.emptyIcon}>🍽️</span>
          {debouncedSearch || activeCategory
            ? 'No dishes match your search. Try a different filter.'
            : 'No food items have been added to the official menu yet. Check back closer to the event!'}
        </div>
      )}

      {/* Detail modal */}
      {renderDetailModal()}
    </div>
  );
}
